import { PageMeta } from "@/components/PageMeta";
import { SectionHeading } from "@/components/SectionHeading";
import { GlassPanel } from "@/components/GlassPanel";
import { CtaLink } from "@/components/Button";
import { Keycap } from "@/components/keycap/Keycap";
import { routeSeo } from "@/content/seo";

const pillars = [
  {
    title: "Organisation-level workspaces",
    body: "Every identity in Devory belongs to an organisation. Team organisations support membership and invitations so engineering work is scoped to the company, not scattered across personal accounts.",
  },
  {
    title: "Roles and permissions",
    body: "Access is enforced through role-aware, deny-by-default authorization rather than inline, ad hoc permission checks scattered through the codebase.",
  },
  {
    title: "Governance",
    body: "Policy evaluation sits at the center of Devory's architecture so organisations can reason about who can do what, and why, as the platform grows.",
  },
  {
    title: "Auditability",
    body: "Devory is built around structured, inspectable operations rather than opaque side effects, with auditability as an explicit architectural goal.",
  },
  {
    title: "Extension control",
    body: "Extensions and third-party integrations are designed to require explicit trust and permission boundaries rather than running with implicit, unrestricted access.",
  },
  {
    title: "Deployment boundaries",
    body: "Deployment and runtime state tracking are part of Devory Cloud's services. Binding these to live, customer-controlled environments is ongoing work, not a finished enterprise capability today.",
  },
];

export function EnterprisePage() {
  const seo = routeSeo["/enterprise"];
  return (
    <>
      <PageMeta title={seo.title} description={seo.description} path="/enterprise" />

      <section className="container page-hero">
        <p className="eyebrow">Enterprise</p>
        <h1 className="page-hero-title">A governed canvas, not an afterthought</h1>
        <p className="page-hero-lede">
          Devory's architecture treats organisations, roles and governance as first-class tiles in
          the canvas itself, not something bolted on later. This page describes what is real today
          and what remains in progress, without inventing SLAs, seat minimums, certifications or
          data-residency guarantees.
        </p>
      </section>

      <section className="container section-tight page-diagram">
        <Keycap id="enterprise" size="xl" state="active" />
      </section>

      <section className="container section">
        <div className="grid-3">
          {pillars.map((pillar) => (
            <GlassPanel as="article" key={pillar.title} className="audience-card">
              <SectionHeading level={3} title={pillar.title} lede={pillar.body} />
            </GlassPanel>
          ))}
        </div>
      </section>

      <section className="container section-tight" aria-labelledby="enterprise-limits-heading">
        <GlassPanel className="problem-card">
          <SectionHeading
            level={2}
            title={<span id="enterprise-limits-heading">What Devory does not claim today</span>}
          />
          <ul className="list-check">
            <li>No specific SLA or uptime commitment is published</li>
            <li>No data residency guarantee is published</li>
            <li>No dedicated support tier is published</li>
            <li>No SOC 2, ISO 27001, PCI DSS or other compliance certification is claimed</li>
          </ul>
        </GlassPanel>
      </section>

      <section className="container section-tight">
        <GlassPanel className="cta-banner">
          <h2>Evaluating Devory for your organisation?</h2>
          <p>
            Enterprise engagements are scoped individually. Tell Devory about your
            organisation&rsquo;s governance, security and deployment requirements.
          </p>
          <div className="cta-banner-actions">
            <CtaLink to="/contact" variant="primary">
              Talk to Devory
            </CtaLink>
            <CtaLink to="/security" variant="secondary">
              Read the security page
            </CtaLink>
          </div>
        </GlassPanel>
      </section>
    </>
  );
}
